import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Smartphone, Share, Plus, X as XIcon } from 'lucide-react';

const STORAGE_KEY = 'tnsp_install_prompt_v1';
const SUPPRESS_DAYS = 30;

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function detectPlatform() {
  const ua = window.navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isAndroid || window.matchMedia?.('(max-width: 767px)').matches;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  return { isIOS, isAndroid, isMobile, isSafari };
}

function shouldShow() {
  if (isStandalone()) return false;
  try {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) return true;
    const ts = parseInt(dismissed, 10);
    if (!ts) return true;
    return Date.now() - ts > SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return true; }
}

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const { isIOS, isAndroid, isMobile, isSafari } = detectPlatform();

  useEffect(() => {
    if (!isMobile) return;

    // Capture Android/Chrome native install event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (shouldShow()) setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS Safari there is no event — show after a short delay if eligible
    if (isIOS && isSafari && shouldShow()) {
      const t = setTimeout(() => setVisible(true), 2500);
      return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', handleBeforeInstall); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, [isMobile, isIOS, isSafari]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  const triggerNativeInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {}
    setDeferredPrompt(null);
    dismiss();
  };

  if (!visible || !isMobile) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pointer-events-none safe-bottom" data-testid="install-prompt">
      <div className="pointer-events-auto mx-auto max-w-sm rounded-2xl shadow-2xl border border-[#9B7ED8] bg-white overflow-hidden">
        <div className="bg-gradient-to-r from-[#6B3FA0] to-[#9B7ED8] px-4 py-3 flex items-center gap-2 text-white">
          <Smartphone className="h-5 w-5 shrink-0" />
          <p className="text-sm font-bold flex-1">Add to Home Screen</p>
          <button onClick={dismiss} aria-label="Dismiss" className="p-1 rounded hover:bg-white/20" data-testid="install-prompt-close">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {isIOS ? (
            <>
              <p className="text-sm text-[#2D1B4E]">Get the TNSP Scheduler as a real app icon on your home screen — no browser bars.</p>
              <ol className="text-xs text-[#5A4D6E] space-y-1.5">
                <li className="flex items-center gap-2">
                  <span className="font-mono bg-[#F0EDF6] text-[#6B3FA0] rounded w-5 h-5 flex items-center justify-center font-bold shrink-0">1</span>
                  Tap <Share className="inline h-3.5 w-3.5 align-middle" /> <strong>Share</strong> in the Safari toolbar
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-mono bg-[#F0EDF6] text-[#6B3FA0] rounded w-5 h-5 flex items-center justify-center font-bold shrink-0">2</span>
                  Choose <Plus className="inline h-3.5 w-3.5 align-middle" /> <strong>Add to Home Screen</strong>
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-mono bg-[#F0EDF6] text-[#6B3FA0] rounded w-5 h-5 flex items-center justify-center font-bold shrink-0">3</span>
                  Tap <strong>Add</strong> in the top-right corner
                </li>
              </ol>
              <Button onClick={dismiss} className="w-full bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="install-prompt-got-it">
                Got it
              </Button>
            </>
          ) : deferredPrompt ? (
            <>
              <p className="text-sm text-[#2D1B4E]">Install TNSP Scheduler as an app for one-tap access from your home screen.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={dismiss} className="flex-1 border-[#DED8EB]" data-testid="install-prompt-later">
                  Later
                </Button>
                <Button onClick={triggerNativeInstall} className="flex-1 bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="install-prompt-install">
                  Install
                </Button>
              </div>
            </>
          ) : isAndroid ? (
            <>
              <p className="text-sm text-[#2D1B4E]">Add TNSP Scheduler to your home screen for quicker access.</p>
              <ol className="text-xs text-[#5A4D6E] space-y-1.5">
                <li>1. Open the <strong>⋮ menu</strong> in Chrome</li>
                <li>2. Tap <strong>Install app</strong> or <strong>Add to Home screen</strong></li>
              </ol>
              <Button onClick={dismiss} className="w-full bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white">Got it</Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
